<div class="assignment-reports">
  @if (isLoading) {
    <div class="assignment-reports__overlay">
      <div class="assignment-reports__spinner" aria-hidden="true"></div>
      <span class="visually-hidden">{{ 'MESSAGES.LOADING' | translate }}</span>
    </div>
  }

  <form
    #reportForm
    class="assignment-reports__form create-project-form"
    [formGroup]="formModel.form"
    (ngSubmit)="saveReport()"
  >
    <div class="assignment-reports__grid">
      <div class="assignment-reports__field">
        <label class="assignment-reports__label form-label required" for="assignment-report-type">
          {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.REPORT_TYPE' | translate }}
        </label>
        <select
          id="assignment-report-type"
          class="form-input assignment-reports__select"
          [class.is-invalid]="formModel.form.controls.reportTypeId.touched && formModel.form.controls.reportTypeId.errors"
          [formControlName]="formModel.fieldNames.reportTypeId"
          [disabled]="isReadonly || isSaving"
        >
          <option value="">
            {{ 'COMPONENTS.ASSIGNMENT_REPORTS.PLACEHOLDERS.REPORT_TYPE' | translate }}
          </option>
          @for (reportType of reportTypes; track reportType.id) {
            <option [value]="reportType.id">{{ reportType.title }}</option>
          }
        </select>
        @if (formModel.form.controls.reportTypeId.touched && formModel.form.controls.reportTypeId.errors) {
          <div class="invalid-feedback">
            {{ 'ERRORS.REQUIRED' | translate }}
          </div>
        }
      </div>

      <div class="assignment-reports__field">
        <label class="assignment-reports__label form-label required" for="assignment-report-value">
          {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.VALUE' | translate }}
        </label>
        <div class="assignment-reports__value-field">
          <input
            id="assignment-report-value"
            type="number"
            step="0.01"
            class="form-input assignment-reports__input"
            [class.is-invalid]="formModel.form.controls.value.touched && formModel.form.controls.value.errors"
            [formControlName]="formModel.fieldNames.value"
            [readonly]="isReadonly || isSaving"
          />
          @if (selectedReportTypeUnits) {
            <span class="assignment-reports__units">{{ selectedReportTypeUnits }}</span>
          }
        </div>
        @if (formModel.form.controls.value.touched && formModel.form.controls.value.errors) {
          <div class="invalid-feedback">
            {{ 'ERRORS.REQUIRED' | translate }}
          </div>
        }
      </div>

      <div class="assignment-reports__field">
        <label class="assignment-reports__label form-label required" for="assignment-report-date">
          {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.REPORT_DATE' | translate }}
        </label>
        <input
          id="assignment-report-date"
          type="date"
          class="form-input assignment-reports__input"
          [class.is-invalid]="formModel.form.controls.reportDate.touched && formModel.form.controls.reportDate.errors"
          [formControlName]="formModel.fieldNames.reportDate"
          [readonly]="isReadonly || isSaving"
        />
        @if (formModel.form.controls.reportDate.touched && formModel.form.controls.reportDate.errors) {
          <div class="invalid-feedback">
            {{ 'ERRORS.REQUIRED' | translate }}
          </div>
        }
      </div>

      <div class="assignment-reports__field assignment-reports__field--full">
        <label class="assignment-reports__label form-label" for="assignment-report-comment">
          {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.COMMENT' | translate }}
        </label>
        <div class="assignment-create-editor form-input assignment-reports__editor-shell">
          <app-custom-quill-editor
            id="assignment-report-comment"
            [formControlName]="formModel.fieldNames.comment"
          ></app-custom-quill-editor>
        </div>
      </div>

      <div class="assignment-reports__field assignment-reports__field--full">
        @if (!isReadonly) {
          <label class="assignment-reports__label form-label">
            {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.FILE' | translate }}
          </label>
          <app-file-upload
            [path]="uploadPath"
            [multiple]="false"
            [isTemporaryFiles]="true"
            [hidden]="false"
            (uploadedFileEvent)="onUploadFile($event)"
          ></app-file-upload>
        }

        @if (pendingAttachment) {
          <div class="assignment-reports__file-pill">
            <span>{{ pendingAttachment.name }} ({{ pendingAttachment.size | fileSize }})</span>
            <button
              type="button"
              class="form-secondary-btn assignment-reports__file-action-btn"
              (click)="removePendingAttachment()"
            >
              {{ 'BUTTONS.REMOVE' | translate }}
            </button>
          </div>
        }

        @if (!pendingAttachment && editAttachment) {
          <div class="assignment-reports__file-pill">
            <span>{{ editAttachment.name }} ({{ editAttachment.size | fileSize }})</span>
            <button
              type="button"
              class="form-secondary-btn assignment-reports__file-action-btn"
              (click)="removeEditAttachment()"
            >
              {{ 'BUTTONS.REMOVE' | translate }}
            </button>
          </div>
        }
      </div>

      <div class="assignment-reports__actions">
        <button
          type="submit"
          class="form-primary-btn"
          [disabled]="isReadonly || isSaving || formModel.form.invalid"
        >
          {{ 'BUTTONS.SAVE_CHANGES' | translate }}
        </button>

        @if (editingReportId) {
          <button
            type="button"
            class="form-secondary-btn"
            [disabled]="isSaving"
            (click)="cancelEdit()"
          >
            {{ 'BUTTONS.CANCEL' | translate }}
          </button>
        }
      </div>
    </div>
  </form>

  <section class="assignment-reports__list-card">
    @if (reportsPage.items.length === 0) {
      <div class="assignment-reports__empty-state">
        {{ 'COMPONENTS.ASSIGNMENT_REPORTS.EMPTY' | translate }}
      </div>
    } @else {
      <div class="assignment-reports__table-wrap">
        <table class="assignment-reports__table">
          <thead>
            <tr class="assignment-reports__table-header">
              <th class="assignment-reports__table-cell assignment-reports__table-cell--date">
                {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.REPORT_DATE' | translate }}
              </th>
              <th class="assignment-reports__table-cell assignment-reports__table-cell--type">
                {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.REPORT_TYPE' | translate }}
              </th>
              <th class="assignment-reports__table-cell assignment-reports__table-cell--value">
                {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.VALUE' | translate }}
              </th>
              <th class="assignment-reports__table-cell assignment-reports__table-cell--comment">
                {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.COMMENT' | translate }}
              </th>
              <th class="assignment-reports__table-cell assignment-reports__table-cell--file">
                {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.FILE' | translate }}
              </th>
              <th class="assignment-reports__table-cell assignment-reports__table-cell--actions">
                {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.ACTIONS' | translate }}
              </th>
            </tr>
          </thead>
          <tbody>
            @for (report of reportsPage.items; track report.id) {
              <tr class="assignment-reports__table-row">
                <td class="assignment-reports__table-cell assignment-reports__table-cell--date">
                  {{ report.reportDate | date: 'yyyy-MM-dd' }}
                </td>
                <td class="assignment-reports__table-cell assignment-reports__table-cell--type assignment-reports__type-cell">
                  {{ report.reportTypeTitle }}
                </td>
                <td class="assignment-reports__table-cell assignment-reports__table-cell--value assignment-reports__value-cell">
                  {{ report.value }} {{ report.units }}
                </td>
                <td class="assignment-reports__table-cell assignment-reports__table-cell--comment">
                  @if (report.sanitizedComment) {
                    <div class="assignment-reports__comment-cell" [innerHTML]="report.sanitizedComment"></div>
                  } @else {
                    <span class="assignment-reports__muted">-</span>
                  }
                </td>
                <td class="assignment-reports__table-cell assignment-reports__table-cell--file">
                  @if (report.attachmentPath && report.attachmentName) {
                    <div class="assignment-reports__file-cell">
                      <span class="assignment-reports__file-name" [attr.title]="report.attachmentName">
                        {{ report.attachmentName }}
                      </span>
                      <button
                        type="button"
                        class="assignment-reports__icon-btn"
                        [attr.title]="'FILE_ACTIONS.DOWNLOAD' | translate"
                        (click)="downloadAttachment(report)"
                      >
                        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M7.33335 1.33331C7.33335 0.965124 7.63183 0.666646 8.00002 0.666646C8.36821 0.666646 8.66669 0.965124 8.66669 1.33331V8.3905L10.8619 6.19524C11.1222 5.93489 11.5444 5.93489 11.8047 6.19524C12.0651 6.45559 12.0651 6.8777 11.8047 7.13805L8.4714 10.4714C8.21105 10.7318 7.78894 10.7318 7.52859 10.4714L4.19526 7.13805C3.93491 6.8777 3.93491 6.45559 4.19526 6.19524C4.45561 5.93489 4.87772 5.93489 5.13807 6.19524L7.33335 8.3905V1.33331ZM2.66669 10.6666C3.03488 10.6666 3.33335 10.9651 3.33335 11.3333V12.6666C3.33335 12.8435 3.40359 13.013 3.52862 13.1381C3.65364 13.2631 3.82321 13.3333 4.00002 13.3333H12C12.1768 13.3333 12.3464 13.2631 12.4714 13.1381C12.5964 13.013 12.6667 12.8435 12.6667 12.6666V11.3333C12.6667 10.9651 12.9652 10.6666 13.3334 10.6666C13.7015 10.6666 14 10.9651 14 11.3333V12.6666C14 13.1971 13.7893 13.7058 13.4142 14.0809C13.0392 14.456 12.5305 14.6666 12 14.6666H4.00002C3.46959 14.6666 2.96088 14.456 2.58581 14.0809C2.21074 13.7058 2.00002 13.1971 2.00002 12.6666V11.3333C2.00002 10.9651 2.2985 10.6666 2.66669 10.6666Z"
                            fill="currentColor"
                          />
                        </svg>
                        <span class="visually-hidden">{{ 'FILE_ACTIONS.DOWNLOAD' | translate }}</span>
                      </button>
                    </div>
                  } @else {
                    <span class="assignment-reports__muted">-</span>
                  }
                </td>
                <td class="assignment-reports__table-cell assignment-reports__table-cell--actions">
                  <div class="assignment-reports__row-actions">
                    @if (!isReadonly) {
                      <button
                        type="button"
                        class="assignment-reports__icon-btn"
                        [attr.title]="'BUTTONS.EDIT' | translate"
                        [disabled]="deletingReportId === report.id || isSaving"
                        (click)="editReport(report)"
                      >
                        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M2 14.5V18H5.5L15.825 7.675L12.325 4.175L2 14.5ZM18.55 4.95C18.9167 4.58333 18.9167 3.99444 18.55 3.65L16.35 1.45C16.0056 1.08333 15.4167 1.08333 15.05 1.45L13.325 3.175L16.825 6.675L18.55 4.95Z"
                            fill="currentColor"
                          />
                        </svg>
                        <span class="visually-hidden">{{ 'BUTTONS.EDIT' | translate }}</span>
                      </button>
                      <button
                        type="button"
                        class="assignment-reports__icon-btn assignment-reports__icon-btn--danger"
                        [attr.title]="'BUTTONS.REMOVE' | translate"
                        [disabled]="deletingReportId === report.id"
                        (click)="deleteReport(report)"
                      >
                        <svg viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M7 16C7.26522 16 7.51957 15.8946 7.70711 15.7071C7.89464 15.5196 8 15.2652 8 15V9C8 8.73478 7.89464 8.48043 7.70711 8.29289C7.51957 8.10536 7.26522 8 7 8C6.73478 8 6.48043 8.10536 6.29289 8.29289C6.10536 8.48043 6 8.73478 6 9V15C6 15.2652 6.10536 15.5196 6.29289 15.7071C6.48043 15.8946 6.73478 16 7 16ZM17 4H13V3C13 2.20435 12.6839 1.44129 12.1213 0.87868C11.5587 0.316071 10.7956 0 10 0H8C7.20435 0 6.44129 0.316071 5.87868 0.87868C5.31607 1.44129 5 2.20435 5 3V4H1C0.734784 4 0.48043 4.10536 0.292893 4.29289C0.105357 4.48043 0 4.73478 0 5C0 5.26522 0.105357 5.51957 0.292893 5.70711C0.48043 5.89464 0.734784 6 1 6H2V17C2 17.7956 2.31607 18.5587 2.87868 19.1213C3.44129 19.6839 4.20435 20 5 20H13C13.7956 20 14.5587 19.6839 15.1213 19.1213C15.6839 18.5587 16 17.7956 16 17V6H17C17.2652 6 17.5196 5.89464 17.7071 5.70711C17.8946 5.51957 18 5.26522 18 5C18 4.73478 17.8946 4.48043 17.7071 4.29289C17.5196 4.10536 17.2652 4 17 4Z"
                            fill="currentColor"
                          />
                        </svg>
                        <span class="visually-hidden">{{ 'BUTTONS.REMOVE' | translate }}</span>
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="assignment-reports__mobile-list">
        @for (report of reportsPage.items; track report.id) {
          <article class="assignment-reports__mobile-card">
            <div class="assignment-reports__mobile-card-header">
              <div class="assignment-reports__mobile-title-block">
                <p class="assignment-reports__mobile-label">
                  {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.REPORT_TYPE' | translate }}
                </p>
                <p class="assignment-reports__mobile-value assignment-reports__mobile-value--strong">
                  {{ report.reportTypeTitle }}
                </p>
              </div>

              <div class="assignment-reports__row-actions">
                @if (!isReadonly) {
                  <div class="assignment-reports__dropdown" ngbDropdown placement="bottom-end" container="body">
                    <button
                      type="button"
                      class="assignment-reports__icon-btn assignment-reports__icon-btn--menu"
                      ngbDropdownToggle
                      [attr.aria-label]="'BUTTONS.ACTIONS' | translate"
                      [disabled]="deletingReportId === report.id || isSaving"
                    >
                      <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M10 4.25C10.9665 4.25 11.75 3.4665 11.75 2.5C11.75 1.5335 10.9665 0.75 10 0.75C9.0335 0.75 8.25 1.5335 8.25 2.5C8.25 3.4665 9.0335 4.25 10 4.25Z" fill="currentColor"/>
                        <path d="M10 11.75C10.9665 11.75 11.75 10.9665 11.75 10C11.75 9.0335 10.9665 8.25 10 8.25C9.0335 8.25 8.25 9.0335 8.25 10C8.25 10.9665 9.0335 11.75 10 11.75Z" fill="currentColor"/>
                        <path d="M10 19.25C10.9665 19.25 11.75 18.4665 11.75 17.5C11.75 16.5335 10.9665 15.75 10 15.75C9.0335 15.75 8.25 16.5335 8.25 17.5C8.25 18.4665 9.0335 19.25 10 19.25Z" fill="currentColor"/>
                      </svg>
                    </button>
                    <div class="assignment-reports-actions-menu" ngbDropdownMenu>
                      <button
                        type="button"
                        class="assignment-reports-actions-menu__item"
                        ngbDropdownItem
                        [disabled]="deletingReportId === report.id || isSaving"
                        (click)="editReport(report)"
                      >
                        {{ 'BUTTONS.EDIT' | translate }}
                      </button>
                      <div class="assignment-reports-actions-menu__divider"></div>
                      <button
                        type="button"
                        class="assignment-reports-actions-menu__item assignment-reports-actions-menu__item--danger"
                        ngbDropdownItem
                        [disabled]="deletingReportId === report.id"
                        (click)="deleteReport(report)"
                      >
                        {{ 'BUTTONS.REMOVE' | translate }}
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>

            <div class="assignment-reports__mobile-grid">
              <div class="assignment-reports__mobile-field">
                <p class="assignment-reports__mobile-label">
                  {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.REPORT_DATE' | translate }}
                </p>
                <p class="assignment-reports__mobile-value">
                  {{ report.reportDate | date: 'yyyy-MM-dd' }}
                </p>
              </div>

              <div class="assignment-reports__mobile-field">
                <p class="assignment-reports__mobile-label">
                  {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.VALUE' | translate }}
                </p>
                <p class="assignment-reports__mobile-value assignment-reports__mobile-value--strong">
                  {{ report.value }} {{ report.units }}
                </p>
              </div>

              <div class="assignment-reports__mobile-field assignment-reports__mobile-field--full">
                <p class="assignment-reports__mobile-label">
                  {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.COMMENT' | translate }}
                </p>
                @if (report.sanitizedComment) {
                  <div class="assignment-reports__mobile-value assignment-reports__comment-cell" [innerHTML]="report.sanitizedComment"></div>
                } @else {
                  <p class="assignment-reports__mobile-value assignment-reports__muted">-</p>
                }
              </div>

              <div class="assignment-reports__mobile-field assignment-reports__mobile-field--full">
                <p class="assignment-reports__mobile-label">
                  {{ 'COMPONENTS.ASSIGNMENT_REPORTS.FIELDS.FILE' | translate }}
                </p>
                @if (report.attachmentPath && report.attachmentName) {
                  <div class="assignment-reports__file-cell">
                    <span class="assignment-reports__file-name" [attr.title]="report.attachmentName">
                      {{ report.attachmentName }}
                    </span>
                    <button
                      type="button"
                      class="assignment-reports__icon-btn"
                      [attr.title]="'FILE_ACTIONS.DOWNLOAD' | translate"
                      (click)="downloadAttachment(report)"
                    >
                      <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M7.33335 1.33331C7.33335 0.965124 7.63183 0.666646 8.00002 0.666646C8.36821 0.666646 8.66669 0.965124 8.66669 1.33331V8.3905L10.8619 6.19524C11.1222 5.93489 11.5444 5.93489 11.8047 6.19524C12.0651 6.45559 12.0651 6.8777 11.8047 7.13805L8.4714 10.4714C8.21105 10.7318 7.78894 10.7318 7.52859 10.4714L4.19526 7.13805C3.93491 6.8777 3.93491 6.45559 4.19526 6.19524C4.45561 5.93489 4.87772 5.93489 5.13807 6.19524L7.33335 8.3905V1.33331ZM2.66669 10.6666C3.03488 10.6666 3.33335 10.9651 3.33335 11.3333V12.6666C3.33335 12.8435 3.40359 13.013 3.52862 13.1381C3.65364 13.2631 3.82321 13.3333 4.00002 13.3333H12C12.1768 13.3333 12.3464 13.2631 12.4714 13.1381C12.5964 13.013 12.6667 12.8435 12.6667 12.6666V11.3333C12.6667 10.9651 12.9652 10.6666 13.3334 10.6666C13.7015 10.6666 14 10.9651 14 11.3333V12.6666C14 13.1971 13.7893 13.7058 13.4142 14.0809C13.0392 14.456 12.5305 14.6666 12 14.6666H4.00002C3.46959 14.6666 2.96088 14.456 2.58581 14.0809C2.21074 13.7058 2.00002 13.1971 2.00002 12.6666V11.3333C2.00002 10.9651 2.2985 10.6666 2.66669 10.6666Z"
                          fill="currentColor"
                        />
                      </svg>
                      <span class="visually-hidden">{{ 'FILE_ACTIONS.DOWNLOAD' | translate }}</span>
                    </button>
                  </div>
                } @else {
                  <p class="assignment-reports__mobile-value assignment-reports__muted">-</p>
                }
              </div>
            </div>
          </article>
        }
      </div>

      <div class="assignment-reports__list-footer">
        <app-paginator
          class="assignment-reports__paginator"
          [totalItems]="reportsPage.totalCount"
          [page]="reportsPage.page"
          [pageSize]="reportsPage.pageSize"
          [showSummary]="true"
          (pageChange)="onPageChange($event)"
          (pageSizeChange)="onChangePageSize($event)"
        />
      </div>
    }
  </section>
</div>

<ng-template #successCreateTpl>
  {{ 'COMPONENTS.ASSIGNMENT_REPORTS.CREATE_SUCCESS' | translate }}
</ng-template>

<ng-template #successDeleteTpl>
  {{ 'COMPONENTS.ASSIGNMENT_REPORTS.DELETE_SUCCESS' | translate }}
</ng-template>

<ng-template #successUpdateTpl>
  {{ 'COMPONENTS.ASSIGNMENT_REPORTS.UPDATE_SUCCESS' | translate }}
</ng-template>

<ng-template #dangerTpl>
  {{ 'COMPONENTS.ASSIGNMENT_REPORTS.ACTION_FAILED' | translate }}
</ng-template>
